import React from 'react';
import PropTypes from 'prop-types';

import RequiredIcon from '@decisiv/ui-components/lib/atoms/RequiredIcon';
import Alert from '@decisiv/ui-components/lib/components/Alert';
import Message from '@decisiv/ui-components/lib/components/Message';
import { H4, P } from '@decisiv/ui-components/lib/components/Typography';

import StepHeader from './StepHeader';
import { PERMISSIONS_STEP } from './copy';
import {
  CheckboxList,
  NestedChoices,
  Section,
  SectionHeading,
  StepBody,
} from './styles';
// Plain Checkbox and RadioGroup do not show their state inside the wizard's
// portal on React 16 — see ./wizardInputs.
import { WizardCheckbox, WizardRadioGroup } from './wizardInputs';

/**
 * Step 4: what the user may do, and who can see them.
 *
 * One permission set is applied to every selected account, which is what the
 * mockup's notice explains — per-account configuration happens afterwards, on the
 * user's own page. The notice only appears for more than one account, since with
 * one there is nothing to distinguish.
 *
 * A permission row is either a single role (Fleet Administrator, Report User) or a
 * group of mutually exclusive ones (Management Role: Member or Manager), which is
 * why the group's roles are radios nested under its checkbox rather than a third
 * checkbox. The role ids come from the server (Management::UserFormOptions), so
 * nothing here hardcodes them.
 *
 * The mockup draws all three rows already ticked. We open blank instead —
 * pre-granting Fleet Administrator is not a default to make on the admin's
 * behalf — which means the "at least one permission" rule is visible on arrival,
 * so it is stated rather than left as an unexplained greyed-out CREATE USER.
 */
function PermissionsStep({ accountCount, draft, onChange, permissionOptions }) {
  const singleRoles = permissionOptions.filter((option) => !option.choices);
  const groups = permissionOptions.filter((option) => option.choices);

  // Mirrors stepValidity[3] in useCreateUserForm: a group's chosen role counts,
  // so ticking only Management Role satisfies this while leaving permissionIds
  // empty. Without this message the step opens with CREATE USER greyed out and
  // nothing on screen saying why.
  const hasPermission =
    draft.permissionIds.length > 0 || Boolean(draft.managementRoleId);

  const toggleRole = (roleId) => {
    const next = draft.permissionIds.includes(roleId)
      ? draft.permissionIds.filter((id) => id !== roleId)
      : [...draft.permissionIds, roleId];

    onChange('permissionIds', next);
  };

  // A group's chosen role lives in its own field rather than in permissionIds,
  // which is what keeps the two roles mutually exclusive without any filtering.
  // Unticking clears it, so a later retick starts from the group's first choice
  // rather than silently reusing a stale one.
  const toggleGroup = (group) => {
    const [first] = group.choices;

    onChange('managementRoleId', draft.managementRoleId ? '' : first.roleId);
  };

  return (
    <StepBody>
      <StepHeader
        showRequiredKey
        subtitle={PERMISSIONS_STEP.subtitle}
        title={PERMISSIONS_STEP.title}
      />

      {accountCount > 1 && (
        <Alert
          intent="information"
          title={PERMISSIONS_STEP.multiAccountNotice}
        />
      )}

      <Section>
        <div>
          <SectionHeading>
            <H4 style={{ margin: 0 }}>{PERMISSIONS_STEP.permissionsTitle}</H4>
            {/* CREATE USER needs one of these, so the section is marked like any
                other required field — which is also what keeps the step's
                "Required Fields" key pointing at something. */}
            <RequiredIcon />
          </SectionHeading>
          <P shade={1} size="small" style={{ margin: 0 }}>
            {PERMISSIONS_STEP.permissionsSubtitle}
          </P>
        </div>
        <CheckboxList>
          {singleRoles.map((option) => (
            <WizardCheckbox
              key={option.id}
              checked={draft.permissionIds.includes(option.roleId)}
              label={option.label}
              name={option.id}
              onChange={() => toggleRole(option.roleId)}
            />
          ))}
          {groups.map((group) => (
            <div key={group.id}>
              <WizardCheckbox
                checked={Boolean(draft.managementRoleId)}
                label={group.label}
                name={group.id}
                onChange={() => toggleGroup(group)}
              />
              {Boolean(draft.managementRoleId) && (
                <NestedChoices>
                  <WizardRadioGroup
                    vertical
                    items={group.choices.map((choice) => ({
                      label: choice.label,
                      value: choice.roleId,
                    }))}
                    name={`${group.id}-choice`}
                    value={draft.managementRoleId}
                    onChange={(event) =>
                      onChange('managementRoleId', event.target.value)
                    }
                  />
                </NestedChoices>
              )}
            </div>
          ))}
        </CheckboxList>
        {!hasPermission && (
          <Message intent="warning">
            {PERMISSIONS_STEP.permissionsRequired}
          </Message>
        )}
      </Section>

      <Section>
        <div>
          <H4 style={{ margin: 0 }}>{PERMISSIONS_STEP.discoverabilityTitle}</H4>
          <P shade={1} size="small" style={{ margin: 0 }}>
            {PERMISSIONS_STEP.discoverabilitySubtitle}
          </P>
        </div>
        <CheckboxList>
          <WizardCheckbox
            checked={draft.hiddenExternal}
            label={PERMISSIONS_STEP.hiddenExternal}
            name="hiddenExternal"
            onChange={() => onChange('hiddenExternal', !draft.hiddenExternal)}
          />
          <WizardCheckbox
            checked={draft.hiddenInternal}
            label={PERMISSIONS_STEP.hiddenInternal}
            name="hiddenInternal"
            onChange={() => onChange('hiddenInternal', !draft.hiddenInternal)}
          />
        </CheckboxList>
      </Section>
    </StepBody>
  );
}

PermissionsStep.propTypes = {
  /** Drives the multi-account notice. */
  accountCount: PropTypes.number,
  draft: PropTypes.shape({
    hiddenExternal: PropTypes.bool,
    hiddenInternal: PropTypes.bool,
    managementRoleId: PropTypes.string,
    permissionIds: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  permissionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      /** Present on a group; its roles are mutually exclusive. */
      choices: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          roleId: PropTypes.string,
        }),
      ),
      id: PropTypes.string,
      label: PropTypes.string,
      roleId: PropTypes.string,
    }),
  ),
};

PermissionsStep.defaultProps = {
  accountCount: 0,
  permissionOptions: [],
};

export default PermissionsStep;
